import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { formatShortDateTime, formatTime, isSameDay } from "@clipboard-health/date-time";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { alpha } from "@mui/material";
import { type AdminMessage } from "@sendbird/chat/message";
import { type CoreMessageType } from "@sendbird/uikit-react/types/utils";
import { isEmpty } from "lodash";

import { APP_V2_APP_EVENTS, logError } from "../lib/analytics";
import { CHIP_COLORS } from "../lib/Theme/constants";
import { type ShiftTimeProposalStatus } from "../ShiftTimeProposals/types";

interface ShiftInviteChipProps {
  color: "success" | "error" | "default" | "warning";
  label: string;
  customStyles?: Record<string, string>;
}

type ShiftTimeProposalChipProps = ShiftInviteChipProps;
type FacilityCanceledMeStatusChipProps = ShiftInviteChipProps;

interface ShiftTimeProposalMessageData {
  shiftTimeProposalId: string;
  originalStartTime: string;
  originalEndTime: string;
}

const GRAY_CHIP_BACKGROUND_COLOR = alpha(CHIP_COLORS.secondary.backgroundColor, 0.3);

export const SHIFT_INVITE_STATUS_CHIP_MAP: Record<ShiftInviteStatus, ShiftInviteChipProps> = {
  [ShiftInviteStatus.PENDING]: {
    color: "warning",
    label: "Pending",
  },
  [ShiftInviteStatus.ACCEPTED]: {
    color: "success",
    label: "Accepted",
  },
  [ShiftInviteStatus.DECLINED]: {
    color: "default",
    label: "Declined",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
  [ShiftInviteStatus.EXPIRED]: {
    color: "default",
    label: "Expired",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
  [ShiftInviteStatus.DELETED]: {
    color: "default",
    label: "Removed",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
  [ShiftInviteStatus.CANCELLED]: {
    color: "default",
    label: "Cancelled",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
};

export const SHIFT_TIME_PROPOSAL_STATUS_CHIP_MAP: Record<
  ShiftTimeProposalStatus,
  ShiftTimeProposalChipProps
> = {
  PROPOSED: {
    color: "warning",
    label: "Pending",
  },
  ACCEPTED: {
    color: "success",
    label: "Accepted",
  },
  APPLIED: {
    color: "success",
    label: "Accepted",
  },
  DECLINED: {
    color: "default",
    label: "Declined",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
  CANCELLED: {
    color: "default",
    label: "Cancelled",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
};

export const EXPIRED_SHIFT_TIME_PROPOSAL_CHIP: ShiftTimeProposalChipProps = {
  color: "default",
  label: "Expired",
  customStyles: {
    backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
  },
};

// FCM approvalStatus is sent over message.data as -1 (pending), 0 (rejected), or 1 (approved)
export const FACILITY_CANCELED_ME_REQUEST_STATUS_CHIP_MAP: Record<
  -1 | 0 | 1,
  FacilityCanceledMeStatusChipProps
> = {
  [-1]: {
    color: "warning",
    label: "Pending",
  },
  0: {
    color: "default",
    label: "Declined",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
  1: {
    color: "success",
    label: "Accepted",
  },
};

export function parseFacilityChatMessage(message: CoreMessageType) {
  let facilityMessage;
  try {
    const hasMessage = "message" in message && typeof message.message === "string";
    if (!hasMessage) {
      return undefined;
    }

    if (isValidJson(message.message)) {
      const parsedMessage = JSON.parse(message.message) as { facility: string };
      facilityMessage = parsedMessage.facility;
    } else {
      facilityMessage = message.message;
    }

    return facilityMessage;
  } catch (error) {
    logError("Failed to parse facility message from chat message", {
      error,
      metadata: { message },
    });
    return undefined;
  }
}

export function parseNumberOfInvitesFromShiftInviteMessage(message: AdminMessage) {
  const hasData = isDefined(message.data) && !isEmpty(message.data);

  if (!hasData || !isValidJson(message.data)) {
    // Extract number of invites from message content as fallback
    const messageContent = message.message;
    const match = /You sent (\d+)\s+shift invites?/.exec(messageContent);
    if (!isDefined(match)) {
      logError(APP_V2_APP_EVENTS.EXTRACT_GROUP_SHIFT_INVITES_QUANTITY_FAILURE, {
        error: new Error("Failed to find regex match for number of shift invites in message"),
        metadata: { message },
      });
      return undefined;
    }
    return Number(match[1]);
  }

  const { numberOfInvites } = JSON.parse(message.data) as { numberOfInvites: number | undefined };

  if (!isDefined(numberOfInvites)) {
    logError(APP_V2_APP_EVENTS.EXTRACT_GROUP_SHIFT_INVITES_QUANTITY_FAILURE, {
      error: new Error("Failed to parse number of shift invites from message"),
      metadata: { message },
    });
    return undefined;
  }

  return numberOfInvites;
}

export function parseShiftInviteChatMessage(message: CoreMessageType) {
  const defaultData = { facilityMessage: undefined, shiftInviteId: undefined, status: undefined };
  try {
    const facilityMessage = parseFacilityChatMessage(message);

    const hasData = isDefined(message.data) && !isEmpty(message.data);
    if (!hasData || !isValidJson(message.data)) {
      return { ...defaultData, facilityMessage };
    }

    const { shiftInviteId, status } = JSON.parse(message.data) as {
      shiftInviteId: string;
      status: ShiftInviteStatus;
    };

    return { facilityMessage, shiftInviteId, status };
  } catch (error) {
    logError("Shift Invite Chat Message Parsing Error", { error, metadata: { message } });
    return defaultData;
  }
}

export function parseVisitInviteChatMessage(message: CoreMessageType): {
  visitId: string | undefined;
} {
  const defaultData = { visitId: undefined };
  try {
    const parsedData: unknown = JSON.parse(message.data);
    if (
      typeof parsedData === "object" &&
      !isNil(parsedData) &&
      "visitId" in parsedData &&
      typeof parsedData.visitId === "string"
    ) {
      return { visitId: parsedData.visitId };
    }

    return defaultData;
  } catch (error) {
    logError("Visit Invite Chat Message Parsing Error", { error, metadata: { message } });
    return defaultData;
  }
}

function isShiftTimeProposalData(data: unknown): data is ShiftTimeProposalMessageData {
  return (
    typeof data === "object" &&
    isDefined(data) &&
    "shiftTimeProposalId" in data &&
    "originalStartTime" in data &&
    "originalEndTime" in data
  );
}

export function parseShiftTimeProposalMessage(message: CoreMessageType) {
  try {
    const parsedData: unknown = JSON.parse(message.data);
    if (isShiftTimeProposalData(parsedData)) {
      return parsedData;
    }
  } catch (error) {
    logError("Shift Invite Chat Message Parsing Error", { error, metadata: { message } });
  }

  return undefined;
}

export function isValidJson(message: string) {
  try {
    JSON.parse(message);
    return true;
  } catch {
    return false;
  }
}

export function formatShiftStartEndDateTime(
  start: Date | string,
  end: Date | string,
  timeZone: string
) {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const formattedStart = formatShortDateTime(startDate, { timeZone });
  const formattedEnd = formatTime(endDate, { timeZone });

  const doesShiftEndOnSameDay = isSameDay(startDate, endDate, { timeZone });

  return `${formattedStart} - ${formattedEnd} ${doesShiftEndOnSameDay ? "" : " (next day)"}`;
}
